import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'

import type {
  SnowflakeRecordWire,
  SnowflakeRowsWire,
} from '../canonicalPicker.types'
import { fieldOf } from './guards'
import { chunksOf, MAX_PAGE_SIZE } from './query'

// Pages of one query read at the same time after the first
export const PARALLEL_PAGES = 4
// Queries read at the same time; a scoped list can need dozens of chunks
export const PARALLEL_QUERIES = 4

const isSnowflakeRowsWire = (value: unknown): value is SnowflakeRowsWire =>
  Array.isArray(fieldOf(value, 'records')) &&
  typeof fieldOf(fieldOf(value, 'pagination'), 'total_count') === 'number'

type QueryResult = { data?: unknown; error?: FetchBaseQueryError }
export type BaseQuery = (url: string) => QueryResult | PromiseLike<QueryResult>
type RowsResult = { data: SnowflakeRowsWire } | { error: FetchBaseQueryError }

const parsingError = (data: unknown): { error: FetchBaseQueryError } => ({
  error: {
    status: 'PARSING_ERROR',
    originalStatus: 200,
    data: String(data),
    error: 'Unexpected snowflake/query response',
  },
})

/** Every page of one query: the first says how many more there are, read a few at a time */
const allPagesOf = async (
  urlOf: (page: number) => string,
  baseQuery: BaseQuery
): Promise<RowsResult> => {
  const first = await baseQuery(urlOf(1))
  if (first.error) return { error: first.error }
  if (!isSnowflakeRowsWire(first.data)) return parsingError(first.data)

  const { total_count: totalCount } = first.data.pagination
  const records = [...first.data.records]
  const pages = Array.from(
    { length: Math.max(0, Math.ceil(totalCount / MAX_PAGE_SIZE) - 1) },
    (_, index) => index + 2
  )
  for (const batch of chunksOf(pages, PARALLEL_PAGES)) {
    const responses = await Promise.all(
      batch.map(page => baseQuery(urlOf(page)))
    )
    for (const { data, error } of responses) {
      if (error) return { error }
      if (!isSnowflakeRowsWire(data)) return parsingError(data)
      records.push(...data.records)
    }
  }
  return { data: { records, pagination: { total_count: totalCount } } }
}

/** Every row of every query, failing as a whole so no row goes missing */
export const allRowsOf = async (
  queries: Array<(page: number) => string>,
  baseQuery: BaseQuery
): Promise<RowsResult> => {
  const records: SnowflakeRecordWire[] = []
  let totalCount = 0
  for (const batch of chunksOf(queries, PARALLEL_QUERIES)) {
    const results = await Promise.all(
      batch.map(urlOf => allPagesOf(urlOf, baseQuery))
    )
    for (const result of results) {
      if ('error' in result) return result
      records.push(...result.data.records)
      totalCount += result.data.pagination.total_count
    }
  }
  return { data: { records, pagination: { total_count: totalCount } } }
}
